'use client';

import { logout } from '@/actions/auth';
import { type Profile, getProfile } from '@/actions/get-profile';
import { SessionContext } from '@/contexts/session';
import { useRouter } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const router = useRouter();

  const loadProfile = useCallback(async () => {
    try {
      setError(null);
      const response = await getProfile();

      if (response.success) {
        setSession(response.data);
        return;
      }

      if (response.message === 'Unauthorized') {
        setSession(null);
        return;
      }

      setError(new Error(response.message));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load profile'));
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteSession = useCallback(async () => {
    try {
      const result = await logout();

      if (result.success) {
        setSession(null);
        setError(null);
        router.push('/sign-in');
      } else {
        setError(new Error(result.message));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to logout'));
    }
  }, [router]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      loadProfile();
    } else {
      setIsLoading(false);
    }
  }, [loadProfile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin-session-logout') {
        setSession(null);
        setError(new Error('Logged out from another tab'));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__refreshAdminSession = loadProfile;
    }
  }, [loadProfile]);

  return (
    <SessionContext.Provider value={{ session, isLoading, error, deleteSession }}>
      {children}
    </SessionContext.Provider>
  );
}

export { useSessionContext as useSession } from '@/contexts/session';
